import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import "./styles.css";
import Container from "../../../components/Container";
import { Table, Button, Input, Space, Modal, notification } from "antd";
import { db } from "../../../services/firebase";
import { collection, getDocs, deleteDoc, doc, query, where } from "firebase/firestore";
import { EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SettingOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";

function ValveList() {
  const [valves, setValves] = useState([]);
  const [valvesFilter, setValvesFilter] = useState([]);

  const location = useLocation();
  const globalSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("search") || "";
  }, [location.search]);

  // 🔎 filtro geral vindo do header (SPL, TAG, Nº Série, Empresa, Unidade, Tipo)
  const applyGlobalFilter = (list) => {
    if (!globalSearch) return list;

    const term = globalSearch.toLowerCase();

    return list.filter((item) => {
      const campos = [
        item.id, // SPL
        item.tag,
        item.numeroSerie,
        item.nomeEmpresa,
        item.unidadeCliente,
        item.tipoValvula,
      ];

      return campos.some((campo) =>
        campo?.toString().toLowerCase().includes(term)
      );
    });
  };

  function handleSearch(search, key) {
    const searchTerm = search?.toLowerCase() || "";
    const baseList = applyGlobalFilter(valves); // mantém o filtro geral
    const data = baseList.filter((item) => {
      const value = item[key];
      return value?.toString().toLowerCase().includes(searchTerm);
    });
    setValvesFilter(search ? data : baseList);
  }

  function reset() {
    // limpa filtro da coluna, mas mantém o filtro geral do header
    setValvesFilter(applyGlobalFilter(valves));
  }

  const getCollectionName = (tipoColecao) => {
    switch (tipoColecao) {
      case "PSV":
        return "valvulasPSV";
      case "Controle":
        return "valvulasControle";
      case "Bloqueio":
        return "valvulasBloqueio";
      default:
        return "";
    }
  };

  // Função para contar certificados vinculados à válvula
  const countLinkedCertificates = async (valvulaId) => {
    const certificadosSnapshot = await getDocs(
      query(collection(db, "certificados"), where("valvula_id", "==", valvulaId))
    );
    return {
      count: certificadosSnapshot.size,
      docs: certificadosSnapshot.docs,
    };
  };

  const handleDelete = async (id, tag, tipoColecao) => {
    const collectionName = getCollectionName(tipoColecao);

    // Buscar certificados vinculados
    const linkedCertificates = await countLinkedCertificates(id);
    const hasCertificates = linkedCertificates.count > 0;

    let contentMessage = `A válvula "${tag}" será removida permanentemente.`;

    if (hasCertificates) {
      contentMessage = (
        <div>
          <p style={{ marginBottom: 12 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            <strong>Atenção!</strong> Esta válvula possui itens vinculados:
          </p>
          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
            <li>{linkedCertificates.count} certificado(s)</li>
          </ul>
          <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
            Todos os certificados serão excluídos permanentemente junto com a válvula "{tag}".
          </p>
        </div>
      );
    }

    Modal.confirm({
      title: "Deseja excluir esta válvula?",
      content: contentMessage,
      okText: hasCertificates ? "Sim, excluir tudo" : "Sim, excluir",
      okType: "danger",
      cancelText: "Cancelar",
      width: hasCertificates ? 500 : 420,
      onOk: async () => {
        try {
          // Excluir certificados vinculados primeiro
          if (hasCertificates) {
            const deletePromises = linkedCertificates.docs.map((docSnap) =>
              deleteDoc(doc(db, "certificados", docSnap.id))
            );
            await Promise.all(deletePromises);
          }

          // Excluir a válvula
          await deleteDoc(doc(db, collectionName, id));

          notification.success({
            message: "Válvula excluída!",
            description: hasCertificates
              ? `A válvula e ${linkedCertificates.count} certificado(s) foram removidos.`
              : "A válvula foi removida com sucesso.",
            placement: "topRight",
          });
          fetchValvesFirebase();
        } catch (error) {
          console.error("Erro ao excluir válvula:", error);
          notification.error({
            message: "Erro ao excluir",
            description: "Não foi possível excluir a válvula.",
            placement: "topRight",
          });
        }
      },
    });
  };

  const columns = [
    {
      title: "Nº",
      dataIndex: "id",
      key: "id",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "id");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "TAG",
      dataIndex: "tag",
      key: "tag",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por TAG"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "tag");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Nº Série",
      dataIndex: "numeroSerie",
      key: "numeroSerie",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº Série"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "numeroSerie");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Empresa",
      dataIndex: "nomeEmpresa",
      key: "nomeEmpresa",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Empresa"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "nomeEmpresa");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidadeCliente",
      key: "unidadeCliente",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Unidade"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "unidadeCliente");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Tipo de Válvula",
      dataIndex: "tipoValvula",
      key: "tipoValvula",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Tipo"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "tipoValvula");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Ações",
      dataIndex: "id",
      key: "actions",
      width: 140,
      render: (id, record) => (
        <div className="actions-cell">
          <Link to={`/admin/cadastro-valvula/${record.tipoColecao}/${id}`} className="action-btn view">
            <EyeOutlined />
          </Link>
          <Link to={`/admin/cadastro-valvula/${record.tipoColecao}/${id}`} className="action-btn edit">
            <EditOutlined />
          </Link>
          <button
            onClick={() => handleDelete(id, record.tag, record.tipoColecao)}
            className="action-btn delete"
          >
            <DeleteOutlined />
          </button>
        </div>
      ),
    },
  ];

  const fetchClientsFirebase = async () => {
    const snapshot = await getDocs(collection(db, "clientes"));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const fetchFromCollection = async (collectionName, tipoColecao) => {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      tipoColecao,
      ...doc.data(),
    }));
  };

  const fetchValvesFirebase = async () => {
    try {
      const [psv, controle, bloqueio] = await Promise.all([
        fetchFromCollection("valvulasPSV", "PSV"),
        fetchFromCollection("valvulasControle", "Controle"),
        fetchFromCollection("valvulasBloqueio", "Bloqueio"),
      ]);

      const allValves = [...psv, ...controle, ...bloqueio];
      const clients = await fetchClientsFirebase();

      const valvesWithClients = allValves.map((valve) => {
        const client = clients.find((c) => c.id === valve.cliente_id);
        return {
          ...valve,
          nomeEmpresa: client?.nomeEmpresa || "Desconhecida",
        };
      });

      const orderedValves = valvesWithClients.sort((a, b) => {
        const dateA = a.dataCadastro?.toDate?.() || new Date(0);
        const dateB = b.dataCadastro?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setValves(orderedValves);
      setValvesFilter(applyGlobalFilter(orderedValves));
    } catch (error) {
      console.error("Erro ao buscar válvulas:", error);
    }
  };

  useEffect(() => {
    fetchValvesFirebase();
  }, []);

  // se o usuário mudar o termo na URL (nova busca pelo header),
  // reaplica o filtro geral
  useEffect(() => {
    setValvesFilter(applyGlobalFilter(valves));
  }, [globalSearch, valves]);

  return (
    <Container>
      <div className="valve-list-container">
        <div className="list-card">
          <div className="list-header">
            <div className="list-header-left">
              <div className="list-icon">
                <SettingOutlined />
              </div>
              <div>
                <h2 className="list-title">Válvulas Cadastradas</h2>
                <p className="list-subtitle">{valvesFilter.length} válvulas encontradas</p>
              </div>
            </div>
          </div>

          <Table
            dataSource={valvesFilter}
            columns={columns}
            rowKey="id"
            className="custom-table"
          />
        </div>

        <Link to="/admin/cadastro-valvula" className="fab-button" title="Nova Válvula">
          <PlusOutlined />
        </Link>
      </div>
    </Container>
  );
}

export default ValveList;
